import { Link } from 'react-router';
import { CalendarClock, CircleCheck, CircleSlash, Clock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/constants';
import { usePortfolio } from '@/services/queries';
import { AccentCard, type AccentTone } from '@/shared/ui/accent-card';
import { SectionHeading, JumpButton } from '@/shared/ui/section-heading';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/lib/utils';
import type { PortfolioSummary } from '@/types';
import { Sparkline, type SparkTone } from './Sparkline';

/**
 * Every licence, by what state it is in.
 *
 * Transcribed from the design: four cards, each with a 52px horizontal gauge -
 * a tinted track, a filled bar, and the number sitting at the right-hand end
 * inside it.
 *
 * ── Why a gauge and not just a number ───────────────────────────────────────
 *
 * Because the number alone has no denominator. "12 expiring" means something
 * different in an estate of fifteen labs and one of four hundred, and the bar
 * is what supplies that without a second figure to read. The four bars are
 * proportions of the same total, so their lengths are comparable across cards -
 * which is the whole point of putting them in a row.
 *
 * ── The states are the ones the server computes ─────────────────────────────
 *
 * There is no `licences.status` column, so these buckets are derived - see
 * `portfolioSummary` in the licensing repository. Two different units:
 *
 * - Active and Not activated count SEATS, by whether a machine fingerprint is
 *   bound to the seat. Their bars are out of every issued seat.
 * - Expiring soon and Expired count LICENCES, by their seats' expiry dates,
 *   since a renewal is bought per licence. Their bars are out of every licence.
 */
interface Stage {
  key: string;
  label: string;
  value: number;
  /** What `value` is a proportion of, and what it counts. */
  of: number;
  unit: 'seats' | 'licences';
  icon: LucideIcon;
  tone: AccentTone;
  bar: string;
  trend: keyof Omit<PortfolioSummary['trends'], 'stepDays'>;
  /** Green only for the healthy count; the other three are licensing work. */
  spark: SparkTone;
}

export function LicencePipeline() {
  const query = usePortfolio(4);
  const summary = query.data;
  const pipeline = summary?.pipeline;
  // Guarded below: an empty estate would divide by zero and render full bars,
  // which is the opposite of the truth.
  const licences = summary?.licences ?? 0;
  const seats = summary?.seatsIssued ?? 0;

  const stages: Stage[] = [
    {
      key: 'active',
      label: 'Active',
      value: pipeline?.active ?? 0,
      of: seats,
      unit: 'seats',
      icon: CircleCheck,
      tone: 'positive',
      bar: 'bg-pill-positive-foreground',
      trend: 'active',
      spark: 'healthy',
    },
    {
      key: 'expiringSoon',
      label: 'Expiring soon (30d)',
      value: pipeline?.expiringSoon ?? 0,
      of: licences,
      unit: 'licences',
      icon: CalendarClock,
      tone: 'caution',
      bar: 'bg-pill-caution-foreground',
      trend: 'expiringSoon',
      spark: 'licensing',
    },
    {
      key: 'expired',
      label: 'Expired',
      value: pipeline?.expired ?? 0,
      of: licences,
      unit: 'licences',
      icon: CircleSlash,
      tone: 'negative',
      bar: 'bg-pill-negative-foreground',
      trend: 'expired',
      spark: 'licensing',
    },
    {
      key: 'notActivated',
      label: 'Not activated',
      value: pipeline?.notActivated ?? 0,
      of: seats,
      unit: 'seats',
      icon: Clock,
      tone: 'brand',
      bar: 'bg-brand-500',
      trend: 'notActivated',
      spark: 'licensing',
    },
  ];

  return (
    <section>
      <SectionHeading
        size="lg"
        action={
          <JumpButton
            label="View the full licence pipeline"
            render={(content, className) => (
              <Link to={ROUTES.customers} className={className}>
                {content}
              </Link>
            )}
          />
        }
      >
        Licence pipeline
      </SectionHeading>

      <div className="grid gap-[18px] sm:grid-cols-2 xl:grid-cols-4">
        {stages.map((stage) => {
          const percent = stage.of > 0 ? Math.round((stage.value / stage.of) * 100) : 0;
          const Icon = stage.icon;

          return (
            <AccentCard
              key={stage.key}
              icon={<Icon size={16} strokeWidth={2} />}
              tone={stage.tone}
              bar="brand"
              title={stage.label}
            >
              {query.isPending ? (
                <Skeleton className="mb-3.5 h-[52px] rounded-[9px]" />
              ) : (
                <div className="relative mb-3.5 h-[52px] overflow-hidden rounded-[9px] bg-muted">
                  <div
                    className={cn('absolute inset-y-0 left-0 rounded-[9px]', stage.bar)}
                    style={{ width: `${String(percent)}%` }}
                    aria-hidden="true"
                  />
                  <span className="absolute top-1/2 right-3.5 -translate-y-1/2 text-[26px] font-bold text-foreground tabular-nums">
                    {stage.value}
                  </span>
                </div>
              )}
              {summary && (
                <Sparkline
                  className="mb-1"
                  points={summary.trends[stage.trend]}
                  stepDays={summary.trends.stepDays}
                  tone={stage.spark}
                  label={stage.label}
                />
              )}
              <p className="sr-only">
                {stage.value} of {stage.of} {stage.unit}
              </p>
            </AccentCard>
          );
        })}
      </div>
    </section>
  );
}
